"use client"

import { useState, useEffect } from 'react'
import { generateHTML } from '@tiptap/html'
import StarterKit from '@tiptap/starter-kit'
import { Color } from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontFamily } from '@tiptap/extension-font-family'
import { Underline } from '@tiptap/extension-underline'
import { TextAlign } from '@tiptap/extension-text-align'
import { FontSize } from './RichTextEditor'
import { TextContent, isRichText, getPlainText, getRichTextJson } from '@/types'

const extensions = [
  StarterKit,
  Color,
  TextStyle,
  FontFamily,
  FontSize,
  Underline,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
]

interface RichTextRendererProps {
  content: TextContent
  className?: string
  style?: React.CSSProperties
}

export function RichTextRenderer({ content, className = '', style }: RichTextRendererProps) {
  const [html, setHtml] = useState<string>('')
  const [mounted, setMounted] = useState(false)

  // Only render on client to avoid hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    // Handle plain string content (backward compatibility)
    if (typeof content === 'string') {
      setHtml(content)
      return
    }

    // Handle rich text content
    if (isRichText(content)) {
      const json = getRichTextJson(content)
      
      try {
        // Generate HTML from Tiptap JSON
        const generatedHtml = generateHTML(json, extensions)
        setHtml(generatedHtml)
      } catch (error) {
        console.error('Error rendering rich text:', error)
        // Fallback to plain text
        setHtml(getPlainText(content))
      }
    } else {
      setHtml(getPlainText(content))
    }
  }, [content, mounted])

  // Show plain text during SSR and until mounted
  if (!mounted) {
    return (
      <div className={className} style={style}>
        {getPlainText(content)}
      </div>
    )
  }

  // Render HTML after mount (client-only)
  if (typeof content === 'string' || !isRichText(content)) {
    return (
      <div className={className} style={style}>
        {html}
      </div>
    )
  }

  return (
    <div 
      className={`rich-text-content ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

